import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, ListPlus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { sdk } from '@/lib/sdk'
import { apiErrorMessage } from '@/lib/checkout/outcome'
import { cn } from '@/lib/utils'
import { trackEvent } from '@/lib/hooks/useGoogleAnalytics'
import type { WaitlistPlace } from '@/types/monetization'

interface JoinWaitlistButtonProps {
  productId: string
  /** Called with the place the API gave once the person is in the line. */
  onJoined?: (place: WaitlistPlace | null) => void
  className?: string
}

/** "Join the waitlist" of a sold-out product (F-74); once in, it says so and where. */
export const JoinWaitlistButton: React.FC<JoinWaitlistButtonProps> = ({ productId, onJoined, className }) => {
  const { t } = useTranslation('products')
  const [busy, setBusy] = useState(false)
  const [place, setPlace] = useState<WaitlistPlace | null | undefined>(undefined)
  const [error, setError] = useState<string | null>(null)

  const join = async () => {
    setBusy(true)
    setError(null)
    try {
      const joined = (await sdk.products.joinWaitlist(productId)) as WaitlistPlace | null
      setPlace(joined ?? null)
      // GA: a waitlist place is a lead for the product (its id only).
      trackEvent('generate_lead', { lead_source: 'waitlist', item_id: productId })
      onJoined?.(joined ?? null)
    } catch (err) {
      const { message } = apiErrorMessage(err)
      setError(message || t('waitlist.joinFailed'))
    } finally {
      setBusy(false)
    }
  }

  if (place !== undefined) {
    return (
      <p role="status" className={cn('flex items-center gap-1.5 text-sm font-medium text-green-700', className)} data-testid="waitlist-joined">
        <CheckCircle2 className="h-4 w-4" />
        {place ? t('waitlist.position', { position: place.position }) : t('waitlist.joined')}
      </p>
    )
  }

  return (
    <div className={className}>
      <Button type="button" variant="outline" size="sm" onClick={() => void join()} disabled={busy} data-testid="waitlist-join">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ListPlus className="mr-2 h-4 w-4" />}
        {t('waitlist.join')}
      </Button>
      {error && <p role="alert" className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
